import { useMemo } from 'react';

import { useTranslations } from 'next-intl';

import UsdcIcon from '@/assets/icons/usdc.svg';
import TokenInfoCard from '@/components/global/TokenInfoCard';
import TransferHistoryCard from '@/components/global/TransferHistoryCard';
import { TransferHistoryCardWithRefund } from '@/components/global/TransferHistoryCard/TransferHistoryCardWithRefund';
import { cn } from '@/lib/utils/cn';
import { formatNumberWithCommasAndDecimals } from '@/lib/utils/formatNumberWithCommasAndDecimals';
import { formatTimestampToGMTString } from '@/lib/utils/formatTimestampToGMTString';
import { getBlockchainPredictionValue } from '@/lib/utils/getBlockchainPredictionValue';
import { useGlobalData } from '@/providers/GlobalDataProvider';
import { T_PredictionWinnerModalType } from '@/ts/types/T_PredictionWinnerModalType';
import { T_UserBlockchainData } from '@/ts/types/T_UserBlockchainData';

import { T_SelectedParticipation } from './useSectionState';

type T_PredictionState = {
  titleKey: string;
  className: string;
};

interface I_UsePredictionsDataProps {
  blockchainData?: T_UserBlockchainData;
  isEmptyState: boolean;
  predictionsStates: T_PredictionState[] | null;
  handleClaimWin: (
    participation: T_UserBlockchainData['predictionEventParticipations'][0]
  ) => Promise<void>;
  handleClaimRefund: (
    participation: T_UserBlockchainData['predictionEventParticipations'][0]
  ) => Promise<void>;
  claimingParticipationId: string | null;
  setDefaultTab: (tab: 'prime' | 'flash') => void;
  setModalType: (type: T_PredictionWinnerModalType) => void;
  setModalVariant: (variant: 'BTC' | 'ETH' | 'SOL' | 'XRP') => void;
  setPredictionAddress: (address: string | null) => void;
  setPredictionId: (id: string | null) => void;
  setSelectedParticipation: (
    participation: T_SelectedParticipation | null
  ) => void;
  setIsModalOpen: (open: boolean) => void;
}

export function usePredictionsData({
  blockchainData,
  isEmptyState,
  predictionsStates,
  handleClaimWin,
  handleClaimRefund,
  claimingParticipationId,
  setDefaultTab,
  setModalType,
  setModalVariant,
  setPredictionAddress,
  setPredictionId,
  setSelectedParticipation,
  setIsModalOpen
}: I_UsePredictionsDataProps) {
  const t = useTranslations();

  const pools = useGlobalData('pools');

  const predictionsInfoList = useMemo(() => {
    if (!predictionsStates) {
      return null;
    }

    return (
      <div className="flex flex-wrap md:flex-row gap-4">
        {predictionsStates.map((state, idx: number) => {
          const count = getBlockchainPredictionValue(
            blockchainData,
            idx,
            isEmptyState
          );

          return (
            <TokenInfoCard
              key={state.titleKey}
              className={cn(
                state.className,
                predictionsStates.length - 1 === idx ? 'md:flex-1' : 'flex-1 '
              )}
              title={t(state.titleKey)}
              Icon={UsdcIcon}
              count={count}
              disabled={isEmptyState}
              isClaim={false}
            />
          );
        })}
      </div>
    );
  }, [predictionsStates, isEmptyState, t, blockchainData]);

  const predictionsHistoryList = useMemo(() => {
    // Only use dynamic data - no fallback to static content.data
    if (!blockchainData?.predictionEventParticipations?.length) {
      return null;
    }

    const participations = blockchainData.predictionEventParticipations;
    return (
      <div className="flex flex-col gap-4">
        {participations.map((participation, idx: number) => {
          const {
            predictionEvent,
            submissions,
            hasWon,
            hasClaimed,
            wonAmountDecimal,
            lostAmountDecimal,
            totalAmountDecimal,
            lastSubmissionTime
          } = participation;
          const { tokenIcon, tokenSymbol, resolved, resolutionTime, canceled } =
            predictionEvent;

          // Check if event is canceled
          const isCanceled = canceled || false;

          const showEyeIcon =
            !isCanceled && // Don't show eye icon for canceled predictions
            (submissions.length > 1 ||
              (submissions.length === 1 &&
                submissions[0]?.predictedPricesDecimal?.length > 1));

          // For canceled status, don't show predictions label (show only tokenSymbol)
          const showPredictionsLabel = isCanceled ? false : showEyeIcon;

          // Find the pool associated with this prediction event to get decimal precision
          // We use predictionContract.contractAddress to match with pool.predictionAddress
          // Default to 2 decimals if pool not found (same behavior as WinnerCard)
          const predictionDecimals =
            pools?.find(
              p =>
                p.predictionAddress?.toLowerCase() ===
                predictionEvent.predictionContract.contractAddress.toLowerCase()
            )?.decimals || 2;

          const predictedPrice =
            !showPredictionsLabel && submissions.length > 0
              ? formatNumberWithCommasAndDecimals(
                  submissions[0]?.predictedPricesDecimal?.[0] || '0',
                  predictionDecimals
                )
              : undefined;

          const dateTime = formatTimestampToGMTString(lastSubmissionTime);

          // Check if resolution time has passed
          // resolutionTime is a string timestamp (in seconds), convert to milliseconds for comparison
          const isResolutionTimePassed = resolutionTime
            ? new Date().getTime() >= parseInt(resolutionTime, 10) * 1000
            : false;

          let status:
            | 'ongoing'
            | 'resolving'
            | 'won'
            | 'lost'
            | 'both'
            | 'canceled';
          if (isCanceled) {
            status = 'canceled';
          } else if (!resolved) {
            // If resolved is false and resolutionTime has passed, show "resolving"
            if (isResolutionTimePassed) {
              status = 'resolving';
            } else {
              status = 'ongoing';
            }
          } else if (hasWon) {
            const lostAmount = lostAmountDecimal
              ? parseFloat(lostAmountDecimal)
              : 0;
            const hasLostAmount = lostAmount > 0;

            if (hasLostAmount) {
              status = 'both'; // Both won and missed
            } else {
              status = 'won';
            }
          } else {
            status = 'lost';
          }

          let wonAmountText: string | undefined;
          let lostAmountText: string | undefined;
          let refundAmountText: string | undefined;

          if ((status === 'won' || status === 'both') && wonAmountDecimal) {
            wonAmountText = `+ ${formatNumberWithCommasAndDecimals(wonAmountDecimal, 2)} USDC`;
          }

          if (status === 'lost' || status === 'both') {
            let missedAmount: string;

            if (lostAmountDecimal) {
              missedAmount = lostAmountDecimal;
            } else if (hasWon) {
              const total = parseFloat(totalAmountDecimal);
              missedAmount = Math.max(0, total - 1).toString();
            } else {
              missedAmount = totalAmountDecimal;
            }

            lostAmountText = `- ${formatNumberWithCommasAndDecimals(missedAmount, 2)} USDC`;
          }

          // For canceled status, refund amount and claim status will be determined by contract call
          // Don't set refundAmountText here - it will be fetched from contract
          // refundAmountText remains undefined for canceled predictions

          const isClaimWin = hasWon && !hasClaimed && resolved && !isCanceled;
          // For canceled status, claim refund availability will be determined by contract call
          // Set initial value, but it will be overridden by contract check
          const isClaimRefund = isCanceled ? true : false;
          const isClaimed = hasWon && hasClaimed && resolved && !isCanceled;
          // For canceled status, claimed status will be determined by contract call
          const isClaimedRefund = isCanceled ? false : false;
          const isCurrentlyClaiming =
            claimingParticipationId === participation.id;

          const tokenVariant =
            (tokenSymbol?.toUpperCase() as 'BTC' | 'ETH' | 'SOL' | 'XRP') ||
            'BTC';

          // Use wrapper component for canceled predictions to fetch refund from contract
          const CardComponent =
            status === 'canceled'
              ? TransferHistoryCardWithRefund
              : TransferHistoryCard;

          return (
            <CardComponent
              key={participation.id || idx}
              tokenIconUrl={tokenIcon?.url}
              tokenIconAlt={tokenIcon?.alt}
              tokenSymbol={tokenSymbol}
              showPredictionsLabel={showPredictionsLabel}
              predictedPrice={predictedPrice}
              dateTime={dateTime}
              status={status}
              wonAmountText={wonAmountText}
              lostAmountText={lostAmountText}
              refundAmountText={refundAmountText}
              isClaimWin={isClaimWin}
              isClaimRefund={isClaimRefund}
              isClaimed={isClaimed}
              isClaimedRefund={isClaimedRefund}
              isClaiming={isCurrentlyClaiming}
              showEyeIcon={showEyeIcon}
              participation={status === 'canceled' ? participation : undefined}
              onClaimWin={() => handleClaimWin(participation)}
              onClaimRefund={() => handleClaimRefund(participation)}
              onViewWinners={() => {
                setDefaultTab('prime');
                setModalType('winners');
                setModalVariant(tokenVariant);
                setPredictionAddress(
                  predictionEvent.predictionContract.contractAddress
                );
                setPredictionId(predictionEvent.predictionId);
                setSelectedParticipation({
                  predictionEvent,
                  hasWon,
                  hasClaimed,
                  claimData: participation.claimData
                });
                setIsModalOpen(true);
              }}
              onViewMissedPrediction={() => {
                setModalType('missed');
                setModalVariant(tokenVariant);
                setPredictionAddress(
                  predictionEvent.predictionContract.contractAddress
                );
                setPredictionId(predictionEvent.predictionId);
                setSelectedParticipation({
                  predictionEvent,
                  lostAmountDecimal
                });
                setIsModalOpen(true);
              }}
              onViewOngoing={() => {
                if (status === 'canceled') {
                  // For canceled predictions, show ongoing modal or handle differently
                  setModalType('ongoing');
                } else {
                  setModalType(
                    status === 'resolving' ? 'resolving' : 'ongoing'
                  );
                }
                setModalVariant(tokenVariant);
                setPredictionAddress(
                  predictionEvent.predictionContract.contractAddress
                );
                setPredictionId(predictionEvent.predictionId);
                setSelectedParticipation({
                  predictionEvent,
                  lostAmountDecimal
                });
                setIsModalOpen(true);
              }}
            />
          );
        })}
      </div>
    );
  }, [
    blockchainData,
    claimingParticipationId,
    handleClaimWin,
    handleClaimRefund,
    setDefaultTab,
    setModalType,
    setModalVariant,
    setPredictionAddress,
    setPredictionId,
    setSelectedParticipation,
    setPredictionId,
    setSelectedParticipation,
    setIsModalOpen,
    pools
  ]);

  return {
    predictionsInfoList,
    predictionsHistoryList
  };
}
